import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { type ITenantFooter } from '@/common/types/tenant-config.types';
import { Divider } from 'antd';
import Button from '@/common/components/button';
import { ResponsiveWrapper } from '@/common/components/wrapper';
import BaleIcon from '~/public/assets/images/svg/socialmedias/Bale.svg';
import EitaaIcon from '~/public/assets/images/svg/socialmedias/Eitaa.svg';
import InstagramIcon from '~/public/assets/images/svg/socialmedias/Instagram.svg';
import RubikaIcon from '~/public/assets/images/svg/socialmedias/Rubika.svg';
import TelegramIcon from '~/public/assets/images/svg/socialmedias/Telegram.svg';
import WhatsappIcon from '~/public/assets/images/svg/socialmedias/Whatsapp.svg';
import FooterNavLink from './footer-nav-link.component';

interface DesktopFooterProps {
  footerData: ITenantFooter;
  isContactUsEnabled: boolean;
}

// eslint-disable-next-line max-lines-per-function
function DesktopFooter({ footerData, isContactUsEnabled }: DesktopFooterProps) {
  const {
    address,
    description,
    pagesStatus,
    logoUrl,
    socialMedias,
    tels,
    mobiles,
    title,
    nemads,
  } = footerData;
  const linkGroup1: Array<{ link: string; title: string }> = [];
  const linkGroup2: Array<{ link: string; title: string }> = [];
  const renderNavGroups = () => {
    if (pagesStatus.contentContactUs) {
      linkGroup1.push({ link: '/contact-us', title: 'تماس با ما' });
    }
    if (pagesStatus.contentAboutUs) {
      linkGroup1.push({ link: '/about-us', title: 'درباره ما' });
    }
    if (pagesStatus.contentPrivacyAndPolicy) {
      linkGroup1.push({ link: '/privacy-policies', title: 'قوانین و مقررات' });
    }
    if (pagesStatus.contentFAQ) {
      linkGroup2.push({ link: '/faq', title: 'سوالات متداول' });
    }
    if (pagesStatus.contentDelivery) {
      linkGroup2.push({ link: '/delivery', title: 'پیگیری سفارش' });
    }
    if (pagesStatus.contentReturn) {
      linkGroup2.push({ link: '/return', title: 'رویه‌های بازگرداندن کالا' });
    }
  };
  renderNavGroups();
  const imageMapper = (socialMedia: string) => {
    switch (socialMedia) {
      case 'telegram':
        return <TelegramIcon />;
      case 'whatsapp':
        return <WhatsappIcon />;
      case 'bale':
        return <BaleIcon />;
      case 'eitaa':
        return <EitaaIcon />;
      case 'instagram':
        return <InstagramIcon />;
      case 'rubika':
        return <RubikaIcon />;
      default:
        return null;
    }
  };
  return (
    <footer className="rounded-t-3xl bg-white p-7">
      <ResponsiveWrapper>
        <div className="flex w-full items-center justify-between pb-10">
          <div className="flex w-full items-center gap-1">
            {logoUrl && (
              <Image src={logoUrl} alt="Vendor Logo" width={40} height={40} />
            )}
            <h3 className="px-2 py-1 text-xl font-semibold text-black-default">
              {title}
            </h3>
          </div>
          {socialMedias ? (
            <div className="flex w-full items-center justify-end gap-3">
              {Object.entries(socialMedias)
                .filter(([_, link]) => !!link)
                .map(([socialMedia, link]) => {
                  if (!link) {
                    return null;
                  }
                  return (
                    <Link
                      href={link}
                      key={socialMedia}
                      target="_blank"
                      className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-silver-200"
                    >
                      <div className="w-5 h-5">{imageMapper(socialMedia)}</div>
                    </Link>
                  );
                })}
            </div>
          ) : null}
        </div>

        <div className="flex w-full flex-nowrap items-start gap-5">
          {/* Description */}
          <div className="w-full max-w-[372px]">
            <p className="max-w-[275px] pb-6 text-right text-base font-medium text-black-400">
              {description}
            </p>
          </div>
          {/* Link Group 1 */}
          {linkGroup1.length ? (
            <div className="w-full max-w-[210px]">
              <FooterNavLink title="درباره فروشگاه" list={linkGroup1} />
            </div>
          ) : null}

          {/* Link Group 2 */}
          {linkGroup2.length ? (
            <div className="w-full max-w-[210px]">
              <FooterNavLink title="خدمات مشتریان" list={linkGroup2} />
            </div>
          ) : null}

          {/* Address */}
          {isContactUsEnabled && (
            <>
              {!address?.length && !tels.length && !mobiles.length ? null : (
                <div className="w-full max-w-[316px]">
                  <h4 className="pb-3 text-lg font-semibold text-black-default md:pb-5">
                    نشانی ما
                  </h4>
                  {address?.length ? (
                    <p className="pb-5 text-base font-medium text-black-400">
                      {address}
                    </p>
                  ) : null}
                  {tels.length ? (
                    <div className="flex items-center justify-between pb-6">
                      <span className="text-base font-medium text-black-400">
                        شماره ثابت:
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          type="link"
                          link={`tel:${tels[0]}`}
                          className="!text-base font-medium"
                        >
                          {tels[0]}
                        </Button>

                        {tels.length > 1 && (
                          <>
                            <span className="text-blue-default">-</span>
                            <Button
                              type="link"
                              link={`tel:${tels[1]}`}
                              className="!text-base font-medium"
                            >
                              {tels[1]}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ) : null}
                  {mobiles.length ? (
                    <div className="flex items-center justify-between pb-6">
                      <span className="text-base font-medium text-black-400">
                        شماره همراه:
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          type="link"
                          link={`tel:${mobiles[0]}`}
                          className="!text-base font-medium"
                        >
                          {mobiles[0]}
                        </Button>
                        {mobiles.length > 1 && (
                          <>
                            <span className="text-blue-default">-</span>
                            <Button
                              type="link"
                              link={`tel:${mobiles[1]}`}
                              className="!text-base font-medium"
                            >
                              {mobiles[1]}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>

        <Divider className="my-10" />
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-1 text-center text-base font-medium text-black-400">
            <span>©</span> تمامی حقوق متعلق به {title} می‌باشد.
          </p>
          {nemads ? (
            <div className="flex items-start justify-center gap-4 pb-6">
              {Object.entries(nemads).map(([nemad, script]) => {
                return (
                  <div
                    className="flex h-[88px] w-[88px] items-center justify-center rounded-lg bg-gray-30 [&_img]:h-[70px] [&_img]:w-[70px] [&_img]:object-contain [&_img]:rounded-lg"
                    key={nemad}
                    dangerouslySetInnerHTML={{ __html: script }}
                  ></div>
                );
              })}
            </div>
          ) : null}
        </div>
        <div className="flex flex-col items-center">
          <span className="flex items-center gap-1 text-xs font-medium text-black-400">
            قدرت گرفته از{' '}
            <Image
              src={'/assets/images/svg/logo-powered.svg'}
              alt="powered by witro"
              width={16}
              height={16}
            />{' '}
            <Link href={'https://witro.ir'}>ویترو</Link>
          </span>
        </div>
      </ResponsiveWrapper>
    </footer>
  );
}

export default DesktopFooter;
